import { jwtDecode } from "jwt-decode";
import { BehaviorSubject } from "rxjs";
import { shareReplay } from "rxjs/operators";

const silentRefreshTimeoutMilliseconds = 10000;

const clientIdOriginMap = {
  "http://localhost:3000": "5e963ea9-e653-4021-ba7f-bbbfa6de842d",
  "https://connectedbuildings-dev.retail-azure.js-devops.co.uk":
    "5e963ea9-e653-4021-ba7f-bbbfa6de842d",
  "https://connectedbuildings-stg.retail-azure.js-devops.co.uk":
    "e2f5a49d-fcf1-421c-b347-87e5577b6299",
  "https://connectedbuildings-prod.retail-azure.js-devops.co.uk":
    "1a2d80c6-a27a-47cb-acf2-08eb3281d758",
  "https://bms.sainsburys.co.uk": "1a2d80c6-a27a-47cb-acf2-08eb3281d758"
};

const authorizationEndpoint =
  "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";

const logoutEndpoint = "https://login.microsoftonline.com/common/oauth2/logout";

const validIssuers = [
  "https://login.microsoftonline.com/e11fd634-26b5-47f4-8b8c-908e466e9bdf/v2.0", // Sainsbury's
  "https://login.microsoftonline.com/5c696c2f-0ccf-48a2-829b-c9dcf4bc3961/v2.0", // Arcus
  "https://login.microsoftonline.com/b87c6325-af6f-46c5-b876-0c025ca9c180/v2.0" // Connected Buildings
];

const scope = "openid email profile user.read";
const redirectUri = window.location.origin + "/sign-in";
const postLogoutRedirectUri = window.location.origin;

const getSavedUrl = () => sessionStorage.getItem("initUrl");

const saveCurrentUrl = () =>
  sessionStorage.setItem("initUrl", window.location.href);

const clearToken = () => localStorage.removeItem("id_token");

const getToken = () => localStorage.getItem("id_token");

const saveToken = token => localStorage.setItem("id_token", token);

const getState = () => sessionStorage.getItem("authorization_state");

const saveState = state => sessionStorage.setItem("authorization_state", state);

const getNonce = () => sessionStorage.getItem("nonce");

const saveNonce = nonce => sessionStorage.setItem("nonce", nonce);

const getBaseAuthorizationUrl = (state, nonce) => {
  const clientId = clientIdOriginMap[window.location.origin];

  return (
    authorizationEndpoint +
    "?" +
    `client_id=${encodeURIComponent(clientId)}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&state=${encodeURIComponent(state)}` +
    `&nonce=${encodeURIComponent(nonce)}` +
    `&scope=${encodeURIComponent(scope)}` +
    `&response_type=id_token`
  );
};

const redirectToAuthorizationEndpoint = () => {
  const state = crypto.randomUUID();
  saveState(state);

  const nonce = crypto.randomUUID();
  saveNonce(nonce);

  const authorizeUrl =
    getBaseAuthorizationUrl(state, nonce) + `&prompt=select_account`;

  saveCurrentUrl();

  window.location.href = authorizeUrl;
};

const handleAuthorizationCallback = hash => {
  const callbackParams = hash
    .substring(1)
    .split("&")
    .reduce((o, p) => {
      const [key, value] = p.split("=").map(c => decodeURIComponent(c));

      return {
        [key]: value,
        ...o
      };
    }, {});

  const expectedState = getState();
  if (expectedState !== callbackParams["state"]) {
    throw new Error("invalid state");
  }

  if (callbackParams.hasOwnProperty("error")) {
    throw new Error(
      callbackParams["error_description"] || callbackParams["error"]
    );
  }

  const jwt = callbackParams["id_token"];

  const decoded = decodeToken(jwt);

  const expectedNonce = getNonce();
  if (expectedNonce !== decoded["nonce"]) {
    throw new Error("invalid nonce");
  }

  const issuer = decoded["iss"];
  const isAuthorizedIssuer = validIssuers.indexOf(issuer) !== -1;
  if (!isAuthorizedIssuer) {
    throw new Error(`invalid issuer: ${issuer}`);
  }

  return jwt;
};

const decodeToken = jwt => jwtDecode(jwt);

const hasTokenExpired = jwt => {
  const decoded = decodeToken(jwt);
  return decoded["exp"] < new Date().valueOf() / 1000;
};

const isInIframe = () => window.self !== window.top;

const refreshTokenSilently = () => {
  let listener;

  return new Promise((resolve, reject) => {
    const loginHint = decodeToken(getToken())["preferred_username"];

    const state = crypto.randomUUID();
    saveState(state);

    const nonce = crypto.randomUUID();
    saveNonce(nonce);

    const url =
      getBaseAuthorizationUrl(state, nonce) +
      `&prompt=none` +
      `&login_hint=${encodeURIComponent(loginHint)}` +
      "&domain_hint=organizations";

    const iframe = document.createElement("iframe");
    iframe.style.visibility = "hidden";
    iframe.src = url;

    listener = e => {
      const message = e.data;
      if (
        message.hasOwnProperty("type") &&
        message["type"] === "TOKEN_REFRESH"
      ) {
        if (message.succeeded) {
          resolve(message.jwt);
        } else {
          reject(message.error);
        }
      }
    };

    window.addEventListener("message", listener);

    document.body.appendChild(iframe);

    setTimeout(() => {
      reject(new Error("timed out refreshing token"));
    }, silentRefreshTimeoutMilliseconds);
  }).finally(() => {
    if (listener) {
      window.removeEventListener("message", listener);
    }
  });
};

const tokensSubject = new BehaviorSubject();
const tokensObservable = tokensSubject.pipe(shareReplay(1));
let haveCreatedAutoRefresh = false;

const signIn = () => {
  return new Promise((resolve, reject) => {
    const isSilentRefresh = isInIframe();

    const hash = window.location.hash;

    const isCallback =
      window.location.href.slice(0, 0 - hash.length) === redirectUri;

    if (isSilentRefresh && isCallback) {
      const sendMessageToParent = message =>
        window.parent.postMessage(message, "*");

      try {
        const jwt = handleAuthorizationCallback(hash);
        saveToken(jwt);

        sendMessageToParent({ type: "TOKEN_REFRESH", succeeded: true, jwt });

        resolve({ jwt, isSilentRefresh: true });
      } catch (e) {
        sendMessageToParent({
          type: "TOKEN_REFRESH",
          succeeded: false,
          error: e
        });

        reject(e);
      }
    } else {
      const existingToken = getToken();
      if (existingToken && !hasTokenExpired(existingToken)) {
        resolve({ jwt: existingToken, isSilentRefresh: false });
        return;
      }

      if (isCallback) {
        try {
          const jwt = handleAuthorizationCallback(hash);

          saveToken(jwt);

          const savedUrl = getSavedUrl();
          window.location.href =
            savedUrl && new URL(savedUrl).origin === window.location.origin
              ? savedUrl
              : "/";
        } catch (e) {
          reject(e);
        }
      } else {
        redirectToAuthorizationEndpoint();
      }
    }
  }).then(r => {
    if (!r.isSilentRefresh && !haveCreatedAutoRefresh) {
      // Create a stream that silently refreshes tokens for as long as it can

      tokensSubject.next(r.jwt);

      tokensSubject.subscribe(jwt => {
        const expires = decodeToken(jwt)["exp"];
        const expiresIn = expires * 1000 - new Date().valueOf();

        // Refresh 5 minutes before the token is due to expire
        const refreshIn = expiresIn - 5 * 60 * 1000;

        setTimeout(() => {
          refreshTokenSilently().then(
            jwt => tokensSubject.next(jwt),
            e => tokensSubject.error(e)
          );
        }, refreshIn);
      });

      haveCreatedAutoRefresh = true;
    }

    return r;
  });
};

const signOutCallbacks = [];

const registerSignOutCallback = callback => signOutCallbacks.push(callback);

const signOut = () => {
  clearToken();

  signOutCallbacks.forEach(fn => fn());

  const logoutUrl =
    logoutEndpoint + `?post_logout_redirect_uri=${postLogoutRedirectUri}`;
  window.location.href = logoutUrl;
};

const getUserId = () => {
  const token = getToken();
  if (token) {
    return decodeToken(token)["sub"];
  }
};

const getUserFullName = () => {
  const token = getToken();
  if (token) {
    return decodeToken(token)["name"];
  }
};

const getTokensObservable = () => tokensObservable;

export {
  signIn,
  signOut,
  getToken,
  getTokensObservable,
  getUserId,
  getUserFullName,
  registerSignOutCallback
};
